import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '@env/environment';

export interface TaskCenterSummary {
  ActionRequiredCount: number;
  CollectionCaseCount?: number;
  OpenWorkItemCount: number;
  InProgressWorkItemCount: number;
  HistoryWorkItemCount: number;
  ProcessCount: number;
  RunningCount: number;
  SucceededLast24Hours: number;
  FailedLast24Hours: number;
}

export interface AdminWorkItem {
  Id: number;
  ProcessExecutionId: string;
  DocumentImportId?: string;
  Title: string;
  Description: string;
  Status: number;
  Priority: number;
  RowVersion: string;
  OriginalFileName?: string;
  AttemptCount: number;
  EmploymentNumber?: number;
  DisplayDate?: string;
  ErrorMessage?: string;
}

export interface ProcessExecution {
  Id: string;
  ProcessType: string;
  Status: number;
  Summary?: string;
  CurrentStep?: string;
  AttemptCount: number;
  CreatedAt: string;
  CompletedAt?: string;
  ErrorMessage?: string;
}

export interface TaskCenterPage<T> {
  Items: T[];
  TotalCount: number;
  PageNumber: number;
  PageSize: number;
}

export interface InkassoCase {
  InvoiceId: number;
  InvoiceNumber: string;
  MemberName: string;
  CustomerName: string;
  Amount: number;
  Currency: string;
  MemberApprovedAt?: string;
  ReadyAt?: string;
  PaymentDue?: string;
  Basis: string;
  StatusName: string;
  ParentInvoice: boolean;
  Version: string;
}

export interface MarkInkassoSentResult {
  Success: boolean;
  AlreadySent: boolean;
  InvoiceId: number;
  CurrentStatus: string;
  Message: string;
}

export interface PendingInkassoDecision {
  StatusId: number;
  StatusName: string;
  InvoiceId: number;
  InvoiceNumber: string;
  MemberName: string;
  CustomerName: string;
  DecisionMailQueuedAt?: string;
  AutoCreditAt?: string;
  AutoCreditEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProcessTaskCenterService {
  private readonly baseUrl = `${env.hostApi}admin/task-center`;

  constructor(private http: HttpClient) { }

  getSummary(): Observable<TaskCenterSummary> {
    return this.http.get<TaskCenterSummary>(`${this.baseUrl}/summary`);
  }

  getPendingDecisions(page: number, pageSize: number): Observable<TaskCenterPage<PendingInkassoDecision>> {
    const params = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));
    return this.http.get<TaskCenterPage<PendingInkassoDecision>>(`${this.baseUrl}/pending-decisions`, { params });
  }

  getInvoiceStatuses(): Observable<any[]> {
    return this.http.get<any[]>(`${env.hostApi}Invoice/statuses`);
  }

  updatePendingDecisionStatus(item: PendingInkassoDecision, status: any): Observable<any> {
    if (['ReadyForCollection', 'CollectionPaused', 'Credited'].includes(status.TechnicalName)) {
      return this.http.put(`${this.baseUrl}/pending-decisions/${item.InvoiceId}/status`, { StatusId: Number(status.Id) });
    }
    const params = new HttpParams().set('statusId', String(status.Id)).set('sendCustomerMessage', 'false');
    return this.http.put(`${env.hostApi}Invoice/${item.InvoiceId}/Status`, null, { params });
  }

  getCollectionCases(page: number, pageSize: number): Observable<TaskCenterPage<InkassoCase>> {
    const params = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));
    return this.http.get<TaskCenterPage<InkassoCase>>(`${this.baseUrl}/collection-cases`, { params });
  }

  markCollectionSent(item: InkassoCase): Observable<MarkInkassoSentResult> {
    return this.http.post<MarkInkassoSentResult>(`${this.baseUrl}/collection-cases/${item.InvoiceId}/mark-sent`, { Version: item.Version });
  }

  getWorkItems(status: string): Observable<TaskCenterPage<AdminWorkItem>> {
    const params = new HttpParams().set('pageSize', '100').set('status', status);
    return this.http.get<TaskCenterPage<AdminWorkItem>>(`${this.baseUrl}/work-items`, { params });
  }

  getProcesses(status = ''): Observable<TaskCenterPage<ProcessExecution>> {
    let params = new HttpParams().set('pageSize', '100');
    if (status) params = params.set('status', status);
    return this.http.get<TaskCenterPage<ProcessExecution>>(`${this.baseUrl}/processes`, { params });
  }

  claim(item: AdminWorkItem): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/claim`, { RowVersion: item.RowVersion });
  }

  retry(item: AdminWorkItem, employmentNumber?: number, displayDate?: string): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/retry`, {
      RowVersion: item.RowVersion,
      EmploymentNumber: employmentNumber,
      DisplayDate: displayDate || undefined
    });
  }

  resolve(item: AdminWorkItem): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/resolve`, { RowVersion: item.RowVersion });
  }

  dismiss(item: AdminWorkItem): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/dismiss`, { RowVersion: item.RowVersion });
  }

  getSource(item: AdminWorkItem): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/work-items/${item.Id}/source`, { responseType: 'blob' });
  }
}
